
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";

export const useLevelRuleScope = (levelId: string, appFilter: Record<string, any>) => {
  // Validate level ID format - Convex IDs are typically alphanumeric with specific length
  const isValidLevelId = levelId && typeof levelId === 'string' && levelId.length > 10;
  
  // Ensure appFilter matches the expected Convex API structure
  // Based on the API definition, it expects a specific format
  const validAppFilter = appFilter && typeof appFilter === 'object' && Object.keys(appFilter).length > 0 
    ? appFilter 
    : { "default": true as const }; // Use proper const assertion for type safety

  console.log('useLevelRuleScope - levelId:', levelId, 'isValid:', isValidLevelId);
  console.log('useLevelRuleScope - appFilter:', validAppFilter);

  // Only run the query if we have a valid level ID
  const levelRules = useConvexQuery(
    isValidLevelId ? api.rules.list.inLevel : undefined,
    isValidLevelId ? {
      level: levelId as any
    } : undefined
  );

  console.log('useLevelRuleScope - raw query result:', levelRules);

  // Handle errors and missing entities gracefully
  const scopedRuleIds = new Set<string>();
  let flatRules: any[] = [];
  let hasQueryError = false;
  let errorMessage = '';
  
  // Check if the query resulted in an error (levelRules will be undefined during loading, but error state is different)
  if (levelRules === null) {
    // Query completed but returned null - this might indicate data integrity issues
    hasQueryError = true;
    errorMessage = 'Failed to load rules for this level. The level may have invalid references.';
    console.error('useLevelRuleScope - Query returned null, possible data integrity issue');
  } else if (Array.isArray(levelRules)) {
    console.log('useLevelRuleScope - processing array with length:', levelRules.length);
    
    try {
      // Handle nested array structure - flatten if needed
      if (levelRules.length > 0 && Array.isArray(levelRules[0])) {
        // It's a nested array like [[rules...], []]
        console.log('useLevelRuleScope - detected nested array structure');
        flatRules = levelRules[0] || [];
        console.log('useLevelRuleScope - flattened to:', flatRules.length, 'rules');
      } else {
        // It's already a flat array
        console.log('useLevelRuleScope - already flat array');
        flatRules = levelRules;
      }
      
      // Extract rule IDs from the flattened rules
      flatRules.forEach((rule, index) => {
        if (rule && rule._id) {
          console.log(`useLevelRuleScope - adding rule ${index + 1}:`, rule._id, rule.name);
          scopedRuleIds.add(rule._id);
        } else {
          console.log(`useLevelRuleScope - skipping invalid rule ${index + 1}:`, rule);
        }
      });
    } catch (processingError) {
      console.error('useLevelRuleScope - Error processing rules array:', processingError);
      hasQueryError = true;
      errorMessage = 'Error processing rules data for this level.';
    }
  }

  console.log('useLevelRuleScope - final scopedRuleIds Set size:', scopedRuleIds.size);
  console.log('useLevelRuleScope - final flatRules length:', flatRules.length);

  return {
    scopedRuleIds,
    rulesData: flatRules, // Return the full rule data
    isLoading: isValidLevelId ? levelRules === undefined : false,
    hasError: !isValidLevelId || hasQueryError,
    errorMessage: !isValidLevelId ? 'Invalid level ID format' : errorMessage
  };
};
